import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  DollarSign,
  Award,
  FileText,
  Building,
  Badge
} from 'lucide-react';
import api from '../utils/axios';
import remountOnId from '../utils/remountOnId';
import toast from 'react-hot-toast';
import Dropdown from '../components/Dropdown';
import { useAuth } from '../contexts/AuthContext';
import useLatestRequest from '../hooks/useLatestRequest';
import { canEditAnyEmployeeField, canEditEmployee } from '../utils/employeePermissions';
import { formatRaise } from '../utils/currency';
import { formatDateOnly } from '../utils/dateUtils';
import { formatStatusText, getStatusBadgeClasses } from '../utils/statusUtils';
import { canReadEmployeeList } from '../utils/employeeListAccess';
import EmployeeDocuments from '../components/documents/EmployeeDocuments';

/**
 * One of the page's per-employee lists, which GET /api/employees/:id does not carry:
 * undefined while loading, the rows once loaded, null once refused or failed (the section
 * hides, with no toast). `request` must be memoised on the employee id.
 */
const useEmployeeList = (enabled, request) => {
  const [rows, setRows] = useState();
  useEffect(() => {
    if (!enabled) return undefined;
    // StrictMode runs this twice and a permission change re-runs it: only the latest
    // request may land.
    let cancelled = false;
    request().then(
      (res) => { if (!cancelled) setRows(Array.isArray(res.data?.data) ? res.data.data : null); },
      () => { if (!cancelled) setRows(null); },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, request]);
  return rows;
};

// Records as a table from the sm breakpoint up. Below it, four columns cannot fit a phone
// (a scrolling table clips the last one, and iOS shows no scrollbar to say so), so each
// record becomes a two-column grid of label over value, as the cards above lay out fields.
const RecordTable = ({ columns, rows }) => (
  <>
    <div className="hidden sm:block overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-900">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th key={col.label} scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map((col) => (
                <td key={col.label} className="px-4 py-2 whitespace-nowrap">{col.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <ul className="sm:hidden divide-y divide-gray-200 border-y border-gray-200">
      {rows.map((row) => (
        <li key={row.id} className="py-3">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            {columns.map((col) => (
              <div key={col.label} className="min-w-0">
                <dt className="text-xs font-medium text-gray-500 uppercase">{col.label}</dt>
                <dd className="mt-1 text-sm text-gray-900 break-words">{col.render(row)}</dd>
              </div>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  </>
);

const SALARY_COLUMNS = [
  { label: 'Amount', render: (s) => `${s.currency === 'USD' ? '$' : '₨'}${Number.parseFloat(s.amount).toLocaleString()}` },
  { label: 'Currency', render: (s) => s.currency },
  // The name, deactivated methods included; the row's payment_method_id is not resolved here.
  { label: 'Payment Method', render: (s) => s.payment_method || '-' },
  { label: 'Effective Date', render: (s) => formatDateOnly(s.effective_date) },
];

const APPRAISAL_COLUMNS = [
  { label: 'Date', render: (a) => formatDateOnly(a.appraisal_date) },
  { label: 'Rating', render: (a) => (a.rating ? `${a.rating}/5` : 'N/A') },
  {
    label: 'Status',
    render: (a) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(a.status, 'appraisal')}`}>
        {formatStatusText(a.status)}
      </span>
    ),
  },
  { label: 'Raise Amount', render: (a) => formatRaise(a.raise_amount, a.currency) },
];

// A list's size, or a placeholder until it loads: a 0 there would be a claim the page
// cannot make yet.
const ListCount = ({ rows, testId }) => (
  <span data-testid={testId} className="text-sm font-medium text-gray-900">
    {rows === undefined ? (
      <>
        <span aria-hidden="true" className="inline-block h-4 w-5 rounded bg-gray-200 animate-pulse align-middle" />
        <span className="sr-only">Loading</span>
      </>
    ) : rows.length}
  </span>
);

const EmployeeDetail = () => {
  const { user, hasPermission } = useAuth();
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [employeePaymentMethods, setEmployeePaymentMethods] = useState([]);
  const [pmForm, setPmForm] = useState({});
  const [pmLoading, setPmLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [showAllBanks, setShowAllBanks] = useState({});
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);

  // Only the latest load may populate the page: StrictMode runs the mount effect twice,
  // an id change starts a new load, and the bank-detail fields are re-fetched after a
  // save. A late response would otherwise overwrite what the user has typed since, or
  // show (and save against) the previous employee.
  const beginPageLoad = useLatestRequest();
  const beginPaymentDetailsLoad = useLatestRequest();

  // Each list comes from its own endpoint and keeps that endpoint's scope check. It is
  // requested only when the server will answer it for this caller and this employee, so a
  // department manager does not draw a sure 403 (and its console errors) on every
  // colleague's page. An all-scope caller or the caller's own page asks at once; a
  // department or reports scope waits for the record to say where the employee sits.
  const subject = employee || { id };
  const readAppraisals = canReadEmployeeList(hasPermission, user, 'appraisals', subject);
  const readDocuments = canReadEmployeeList(hasPermission, user, 'documents', subject);
  const requestAppraisals = useCallback(() => api.get(`/api/appraisals/employee/${id}/history`), [id]);
  const appraisals = useEmployeeList(readAppraisals, requestAppraisals);
  // The documents folder loads its own list (EmployeeDocuments) and reports it here for
  // the count: undefined while loading, null once refused or failed.
  const [documents, setDocuments] = useState();

  // The loaders are memoised on `id` (and the stable useLatestRequest handles), so the
  // load effect below runs once per record, as remountOnId gives each record its own
  // instance anyway.
  const fetchEmployee = useCallback(async (isCurrent) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/employees/${id}`);
      if (!isCurrent()) return;
      setEmployee(response.data.data);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching employee:', error);
      toast.error('Failed to load employee details');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [id]);

  const fetchPaymentMethods = useCallback(async (isCurrent) => {
    try {
      setPaymentMethodsLoading(true);
      const res = await api.get('/api/payment-methods');
      if (!isCurrent()) return;
      setPaymentMethods(res.data.data);
    } catch (err) {
      if (isCurrent()) console.error('Error fetching payment methods:', err);
    } finally {
      if (isCurrent()) setPaymentMethodsLoading(false);
    }
  }, []);

  const fetchEmployeePaymentMethods = useCallback(async () => {
    const isCurrent = beginPaymentDetailsLoad();
    try {
      const res = await api.get(`/api/employees/${id}/payment-methods`);
      if (!isCurrent()) return;
      setEmployeePaymentMethods(res.data.data);
      // Set form state for each
      const formState = {};
      res.data.data.forEach(pm => {
        formState[Number(pm.payment_method_id)] = {
          account_number: pm.account_number || '',
          account_title: pm.account_title || '',
          bank_id: pm.bank_id || '',
          bank_name: pm.bank_name || ''
        };
      });
      setPmForm(formState);
    } catch (err) {
      console.error('Error fetching employee payment methods:', err);
      // ignore
    }
  }, [beginPaymentDetailsLoad, id]);

  useEffect(() => {
    const isCurrent = beginPageLoad();
    fetchEmployee(isCurrent);
    fetchPaymentMethods(isCurrent);
    fetchEmployeePaymentMethods();
  }, [beginPageLoad, fetchEmployee, fetchPaymentMethods, fetchEmployeePaymentMethods]);

  const handlePmInputChange = (pmId, field, value) => {
    setPmForm(prev => ({
      ...prev,
      [pmId]: {
        ...prev[pmId],
        [field]: value
      }
    }));
  };

  const handlePmSave = async (pmId) => {
    setPmLoading(true);
    try {
      const currentFormData = pmForm[pmId] || {};
      await api.put(`/api/employees/${id}/payment-methods`, {
        payment_method_id: pmId,
        account_number: currentFormData.account_number,
        account_title: currentFormData.account_title,
        bank_id: currentFormData.bank_id || null,
      });
      toast.success('Payment method details updated');
      
      // Refresh the employee payment methods data
      await fetchEmployeePaymentMethods();
    } catch (err) {
      toast.error('Failed to update payment method details');
    } finally {
      setPmLoading(false);
    }
  };

  const fetchBanks = async () => {
    if (banks.length === 0) {
      try {
        const res = await api.get('/api/banks');
        setBanks(res.data.data);
      } catch (err) {
        // ignore
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Employee not found</h3>
        <p className="mt-1 text-sm text-gray-500">The employee you're looking for doesn't exist.</p>
        <Link
          to="/employees"
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-primary-100 hover:bg-primary-200"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Employees
        </Link>
      </div>
    );
  }

  // update_all: anyone; update_department: someone else in the viewer's department.
  const canEdit = canEditEmployee(hasPermission, user, employee);
  // The bank-details summary: one row per payment method, with what this employee has on file.
  const accountFor = (pm) => employeePaymentMethods.find(e => Number(e.payment_method_id) === Number(pm.id));
  const notSet = <span className="text-gray-400">Not set</span>;
  const paymentColumns = [
    { label: 'Method', render: (pm) => pm.name },
    { label: 'Account Number', render: (pm) => accountFor(pm)?.account_number || notSet },
    { label: 'Account Title', render: (pm) => accountFor(pm)?.account_title || notSet },
    { label: 'Bank', render: (pm) => accountFor(pm)?.bank_name || notSet },
  ];
  const showAppraisals = readAppraisals && appraisals !== null;
  const showDocuments = readDocuments && documents !== null;
  // The server drops the `salaries` key (with every other salary field) when this caller may
  // not see this employee's pay, so an absent key means "hidden", never "none". Visible pay
  // always arrives as an array, empty when there are no salary records.
  const showSalaries = Array.isArray(employee.salaries);
  const anyStats = showSalaries || showAppraisals || showDocuments;

  return (
    <div className="space-y-6">
      {/* Header: the Edit button drops to its own line on a phone rather than squeezing the name. */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to="/employees"
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {employee.first_name} {employee.last_name}
            </h1>
            <p className="text-gray-600">{employee.position}</p>
          </div>
        </div>
        {user?.employee_id !== Number(id) && (
          <Link
            to={canEdit ? `/employees/${id}/edit` : '#'}
            data-testid="employee-detail-edit"
            aria-disabled={!canEdit}
            className={`inline-flex items-center whitespace-nowrap px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
              canEdit
                ? 'text-white bg-primary-600 hover:bg-primary-700'
                : 'text-gray-400 bg-gray-300 cursor-not-allowed'
            }`}
            title={canEdit ? 'Edit Employee' : 'You do not have permission to edit employees'}
            onClick={(e) => {
              if (!canEdit) {
                e.preventDefault();
              }
            }}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Employee
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Employee Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <Badge className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Employee ID</p>
                  <p data-testid="employee-detail-employee-id" className="text-sm text-gray-900">{employee.employee_id}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Badge className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">CNIC</p>
                  <p data-testid="employee-detail-cnic" className="text-sm text-gray-900">{employee.cnic || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Badge className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Taxable Employee</p>
                  <p className="text-sm text-gray-900">{employee.taxable ? 'Yes' : 'No'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Department</p>
                  <p data-testid="employee-detail-department" className="text-sm text-gray-900">{employee.department_name || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Work email</p>
                  <p data-testid="employee-detail-email" className="text-sm text-gray-900">{employee.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Personal email</p>
                  <p data-testid="employee-detail-personal-email" className="text-sm text-gray-900">{employee.personal_email || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Phone</p>
                  <p data-testid="employee-detail-phone" className="text-sm text-gray-900">{employee.phone || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Date of Birth</p>
                  <p className="text-sm text-gray-900">
                    {employee.date_of_birth ? formatDateOnly(employee.date_of_birth) : 'Not provided'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Hire Date</p>
                  <p data-testid="employee-detail-hire-date" className="text-sm text-gray-900">{formatDateOnly(employee.hire_date)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Documents, high on the page: the folder is worked from here (upload, review), and
              under the payment forms it was a long scroll away. */}
          {readDocuments && (
            <EmployeeDocuments
              employeeId={employee.id}
              employeeName={`${employee.first_name} ${employee.last_name}`}
              onLoaded={setDocuments}
            />
          )}

          {/* Manager Information */}
          {employee.manager_first_name && (
            <div className="bg-white rounded-lg shadow-soft p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Manager Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Manager Name</p>
                    <p className="text-sm text-gray-900">
                      {employee.manager_first_name} {employee.manager_last_name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Manager Employee ID</p>
                    <p className="text-sm text-gray-900">{employee.manager_employee_id}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Manager Email</p>
                    <p className="text-sm text-gray-900">{employee.manager_email}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Address Information */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h2>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Address</p>
                  <p className="text-sm text-gray-900">
                    {employee.address ? (
                      <>
                        {employee.address}<br />
                        {employee.city}, {employee.country}
                      </>
                    ) : (
                      'Not provided'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Name</p>
                <p className="text-sm text-gray-900">{employee.emergency_contact_name || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Phone</p>
                <p className="text-sm text-gray-900">{employee.emergency_contact_phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Relationship</p>
                <p className="text-sm text-gray-900">{employee.emergency_contact_relationship || 'Not provided'}</p>
              </div>
            </div>
          </div>
          {/* Payment Method Details */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Method Details</h2>
            {paymentMethodsLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading payment methods...</p>
              </div>
            ) : paymentMethods && paymentMethods.length > 0 ? (
              <>
                {/* Spaced from the edit fields below, when there are any. */}
                <div className={canEditAnyEmployeeField(hasPermission) ? 'mb-6' : undefined}>
                  <RecordTable columns={paymentColumns} rows={paymentMethods} />
                </div>
                {/* Edit Fields: PUT /api/employees/:id/payment-methods requires update_all, so
                    update_own (every employee, on their own record) and update_department
                    (department managers) must not show them. */}
                {canEditAnyEmployeeField(hasPermission) && paymentMethods.map(pm => {
                  const empPm = employeePaymentMethods.find(e => Number(e.payment_method_id) === Number(pm.id));
                  return (
                    <div key={pm.id} className="mb-6 border-b pb-4 last:border-b-0 last:pb-0">
                      <div className="font-medium text-gray-700 mb-2">{pm.name}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pm.requires_account_number && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Account Number</label>
                            <input
                              type="text"
                              className="mt-1 w-full border rounded-md px-3 py-2"
                              value={pmForm[pm.id]?.account_number || ''}
                              onChange={e => handlePmInputChange(pm.id, 'account_number', e.target.value)}
                            />
                          </div>
                        )}
                        {pm.requires_account_title && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Account Title</label>
                            <input
                              type="text"
                              className="mt-1 w-full border rounded-md px-3 py-2"
                              value={pmForm[pm.id]?.account_title || ''}
                              onChange={e => handlePmInputChange(pm.id, 'account_title', e.target.value)}
                            />
                          </div>
                        )}
                        {pm.requires_bank_name && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Bank</label>
                            <Dropdown
                              className="mt-1"
                              value={pmForm[pm.id]?.bank_id || ''}
                              onChange={e => handlePmInputChange(pm.id, 'bank_id', e.target.value)}
                              onFocus={async () => {
                                await fetchBanks();
                                setShowAllBanks(prev => ({ ...prev, [pm.id]: true }));
                              }}
                              onClick={async () => {
                                await fetchBanks();
                                setShowAllBanks(prev => ({ ...prev, [pm.id]: true }));
                              }}
                              onBlur={() => setShowAllBanks(prev => ({ ...prev, [pm.id]: false }))}
                            >
                              <option value="">Select bank</option>
                              {showAllBanks[pm.id]
                                ? banks.map(bank => (
                                    <option key={bank.id} value={bank.id}>{bank.name}</option>
                                  ))
                                : empPm && empPm.bank_id && empPm.bank_name
                                ? [<option key={empPm.bank_id} value={empPm.bank_id}>{empPm.bank_name}</option>]
                                : null}
                            </Dropdown>
                          </div>
                        )}
                      </div>
                      {(pm.requires_account_number || pm.requires_bank_name || pm.requires_account_title) && (
                        <button
                          type="button"
                          onClick={() => handlePmSave(pm.id)}
                          disabled={pmLoading}
                          className="mt-2 bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 disabled:opacity-50"
                        >
                          {pmLoading ? 'Saving...' : 'Save'}
                        </button>
                      )}
                      {!(pm.requires_account_number || pm.requires_bank_name || pm.requires_account_title) && (
                        <p className="text-sm text-gray-500 mt-2">No configuration required for this payment method.</p>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No payment methods available.</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Employee Status */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-500">Employment Status</p>
                <span data-testid="employee-detail-employment-status" className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  employee.employment_status === 'active'
                    ? 'bg-success-100 text-success-800'
                    : employee.employment_status === 'inactive'
                    ? 'bg-warning-100 text-warning-800'
                    : 'bg-danger-100 text-danger-800'
                }`}>
                  {employee.employment_status}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Employment Type</p>
                <p data-testid="employee-detail-employment-type" className="text-sm text-gray-900">{employee.employment_type || 'Full-time'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Daily Target Hours Exception</p>
                <p data-testid="employee-detail-daily-target-hours" className="text-sm text-gray-900">
                  {employee.daily_target_hours != null
                    ? `${Number(employee.daily_target_hours)} h`
                    : 'Follows the attendance policy'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Attendance Exempt</p>
                <p data-testid="employee-detail-attendance-exempt" className="text-sm text-gray-900">
                  {employee.attendance_exempt ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Gender</p>
                <p className="text-sm text-gray-900">{employee.gender || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Quick Stats and Quick Actions: each row only for a record set this caller may see. */}
          {anyStats && (
            <div className="bg-white rounded-lg shadow-soft p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
              <div className="space-y-4">
                {showSalaries && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Salary Records</span>
                    </div>
                    <span data-testid="employee-detail-salaries-count" className="text-sm font-medium text-gray-900">
                      {employee.salaries.length}
                    </span>
                  </div>
                )}
                {showAppraisals && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Award className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Appraisals</span>
                    </div>
                    <ListCount rows={appraisals} testId="employee-detail-appraisals-count" />
                  </div>
                )}
                {showDocuments && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Documents</span>
                    </div>
                    <ListCount rows={documents} testId="employee-detail-documents-count" />
                  </div>
                )}
              </div>
            </div>
          )}

          {anyStats && (
            <div className="bg-white rounded-lg shadow-soft p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-2">
                {showSalaries && (
                  <Link
                    to={`/salaries?employee_id=${employee.id}`}
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    View Salaries
                  </Link>
                )}
                {showAppraisals && (
                  <Link
                    to={`/appraisals?employee_id=${employee.id}`}
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Award className="h-4 w-4 mr-2" />
                    View Appraisals
                  </Link>
                )}
                {showDocuments && (
                  <a
                    href="#documents"
                    data-testid="employee-detail-view-documents"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('documents')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Documents
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Render related records if present */}
      {showSalaries && employee.salaries.length > 0 && (
        <div data-testid="employee-detail-salaries" className="bg-white rounded-lg shadow-soft p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Salary Records</h2>
          <RecordTable columns={SALARY_COLUMNS} rows={employee.salaries} />
        </div>
      )}
      {showAppraisals && appraisals?.length > 0 && (
        <div data-testid="employee-detail-appraisals" className="bg-white rounded-lg shadow-soft p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Appraisals</h2>
          <RecordTable columns={APPRAISAL_COLUMNS} rows={appraisals} />
        </div>
      )}
    </div>
  );
};

// One instance per record: see utils/remountOnId.js.
export default remountOnId(EmployeeDetail);
